import type React from "react";

import { saveUserInfoOnly, checkUserTypeByEmail } from "@src/api";
import { aiMatchUuidAtom } from "@src/state";
import { UserInfo } from "@src/types/userInfo";
import { X } from "lucide-react";
import { useState } from "react";
import { useRecoilValue } from "recoil";
import {
	companyIndustryOptionArray,
	companySizeOptionArray,
	positionOptionArray,
	routeOptionArray,
} from "../../model/signupAndApplyModel";
import { Button } from "./base/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./base/dialog";
import { Input } from "./base/input";
import { Label } from "./base/label";
import { Select } from "./base/select";

interface LeadCaptureModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: UserInfo) => void;
}

export function LeadCaptureModal({ isOpen, onClose, onSubmit }: LeadCaptureModalProps) {
	const [formData, setFormData] = useState({
		name: "",
		phone: "",
		email: "",
		companyName: "",
		industry: "",
		companySize: "",
		position: "",
		route: "",
		routeDetail: "",
		inviteCode: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	const matchUuid = useRecoilValue(aiMatchUuidAtom);

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		if (!formData.companyName.trim()) {
			newErrors.companyName = "회사명을 입력해주세요";
		}

		if (!formData.name.trim()) {
			newErrors.name = "이름을 입력해주세요";
		}

		if (!formData.phone.trim()) {
			newErrors.phone = "전화번호를 입력해주세요";
		} else if (!/^[0-9-+\s()]+$/.test(formData.phone)) {
			newErrors.phone = "올바른 전화번호를 입력해주세요";
		}

		if (!formData.email.trim()) {
			newErrors.email = "이메일을 입력해주세요";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = "올바른 이메일을 입력해주세요";
		}

		if (!formData.industry) {
			newErrors.industry = "산업군을 선택해주세요";
		}

		if (!formData.companySize) {
			newErrors.companySize = "기업규모를 선택해주세요";
		}

		if (!formData.position) {
			newErrors.position = "직책을 선택해주세요";
		}

		if (!formData.route) {
			newErrors.route = "경로를 선택해주세요";
		}

		if (formData.route === "기타" && !formData.routeDetail.trim()) {
			newErrors.routeDetail = "경로 상세를 입력해주세요";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (validateForm()) {
			// 이메일로 사용자 타입 확인
			const emailCheckResult = await checkUserTypeByEmail(formData.email);
			if (emailCheckResult.isExpert) {
				setErrors({ email: emailCheckResult.message });
				return;
			}

			const submitData = {
				...formData,
				routeDetail: formData.route === "기타" ? formData.routeDetail : "",
			};
			onSubmit(submitData);
			setFormData({
				name: "",
				phone: "",
				email: "",
				companyName: "",
				industry: "",
				companySize: "",
				position: "",
				route: "",
				routeDetail: "",
				inviteCode: "",
			});
			setErrors({});
		}
	};

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-md bg-white max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<div className="flex items-center justify-between">
						<DialogTitle className="text-xl font-semibold">
							상담을 하려면 기본 정보가 필요해요!
						</DialogTitle>
						<Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
							<X className="h-4 w-4" />
						</Button>
					</div>
				</DialogHeader>

				<div className="space-y-4">
					<p className="text-sm text-slate-600">기본 정보를 입력해주세요</p>

					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="companyName">회사명 *</Label>
							<Input
								id="companyName"
								type="text"
								placeholder="회사명"
								value={formData.companyName}
								onChange={(e) => handleInputChange("companyName", e.target.value)}
								className={errors.companyName ? "border-red-500" : ""}
							/>
							{errors.companyName && (
								<p className="text-sm text-red-500">{errors.companyName}</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="name">이름 *</Label>
							<Input
								id="name"
								type="text"
								placeholder="홍길동"
								value={formData.name}
								onChange={(e) => handleInputChange("name", e.target.value)}
								className={errors.name ? "border-red-500" : ""}
							/>
							{errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
						</div>

						<div className="space-y-2">
							<Label htmlFor="phone">전화번호 *</Label>
							<Input
								id="phone"
								type="tel"
								placeholder="010-1234-5678"
								value={formData.phone}
								onChange={(e) => handleInputChange("phone", e.target.value)}
								className={errors.phone ? "border-red-500" : ""}
							/>
							{errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
						</div>

						<div className="space-y-2">
							<Label htmlFor="email">이메일 *</Label>
							<Input
								id="email"
								type="email"
								placeholder="example@company.com"
								value={formData.email}
								onChange={(e) => handleInputChange("email", e.target.value)}
								className={errors.email ? "border-red-500" : ""}
							/>
							{errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
						</div>

						<div className="space-y-2">
							<Label htmlFor="position">직책 *</Label>
							<Select
								id="position"
								value={formData.position}
								onChange={(e) => handleInputChange("position", e.target.value)}
								error={!!errors.position}
							>
								<option value="">직책을 선택해주세요</option>
								{positionOptionArray.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</Select>
							{errors.position && (
								<p className="text-sm text-red-500">{errors.position}</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="industry">산업군 *</Label>
							<Select
								id="industry"
								value={formData.industry}
								onChange={(e) => handleInputChange("industry", e.target.value)}
								error={!!errors.industry}
							>
								<option value="">산업군을 선택해주세요</option>
								{companyIndustryOptionArray.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</Select>
							{errors.industry && (
								<p className="text-sm text-red-500">{errors.industry}</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="companySize">기업규모 *</Label>
							<Select
								id="companySize"
								value={formData.companySize}
								onChange={(e) => handleInputChange("companySize", e.target.value)}
								error={!!errors.companySize}
							>
								<option value="">기업규모를 선택해주세요</option>
								{companySizeOptionArray.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</Select>
							{errors.companySize && (
								<p className="text-sm text-red-500">{errors.companySize}</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="route">알게 된 경로 *</Label>
							<Select
								id="route"
								value={formData.route}
								onChange={(e) => handleInputChange("route", e.target.value)}
								error={!!errors.route}
							>
								<option value="">알게 된 경로를 선택해주세요</option>
								{routeOptionArray.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</Select>
							{errors.route && <p className="text-sm text-red-500">{errors.route}</p>}
						</div>

						{formData.route === "기타" && (
							<div className="space-y-2">
								<Label htmlFor="routeDetail">경로 상세 *</Label>
								<Input
									id="routeDetail"
									type="text"
									placeholder="경로를 입력해주세요"
									value={formData.routeDetail}
									onChange={(e) =>
										handleInputChange("routeDetail", e.target.value)
									}
									className={errors.routeDetail ? "border-red-500" : ""}
								/>
								{errors.routeDetail && (
									<p className="text-sm text-red-500">{errors.routeDetail}</p>
								)}
							</div>
						)}

						<div className="space-y-2">
							<Label htmlFor="inviteCode">추천코드 (선택사항)</Label>
							<Input
								id="inviteCode"
								type="text"
								placeholder="추천코드를 입력해주세요"
								value={formData.inviteCode}
								onChange={(e) => handleInputChange("inviteCode", e.target.value)}
								className={errors.inviteCode ? "border-red-500" : ""}
							/>
							{errors.inviteCode && (
								<p className="text-sm text-red-500">{errors.inviteCode}</p>
							)}
						</div>

						<Button
							type="submit"
							className="w-full bg-blue-600 hover:bg-blue-700 text-white"
							id="lead-capture-modal-submit-button"
						>
							상담 시작하기
						</Button>
					</form>

					<p className="text-xs text-slate-500 text-center">
						입력하신 정보는 상담 목적으로 사용되며, 안전하게 보호됩니다.
					</p>
				</div>
			</DialogContent>
		</Dialog>
	);
}
